(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────
  // Runtime config is read from the widget root's data-* attributes, which
  // the `remi-widget.liquid` snippet emits. Nothing store-specific is
  // hardcoded here so the same asset ships to every environment.
  var VF_URL = 'https://general-runtime.voiceflow.com';
  // The Remi session server (remi-session/ in the agent repo). The snippet's
  // data-session-url overrides it.
  var SESSION_URL = 'https://remi-session.vercel.app/api/session';

  // --- Visitor ID (consent-aware) ---
  // The ID ties the visitor's turns to one Voiceflow conversation. It is only
  // created when the customer starts a chat (never on page load), and where it
  // is kept depends on cookie consent:
  //   - preferences consent given -> localStorage, so the chat survives visits
  //   - otherwise                 -> sessionStorage, gone when the tab closes
  //     (a stale localStorage ID from before consent was withdrawn is removed)
  // Storage that throws (private mode, blocked site data) falls back to an
  // in-memory ID for this page view.
  var UID_KEY = 'remi_uid';

  function newUserID() {
    var arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    arr[6] = (arr[6] & 0x0f) | 0x40;
    arr[8] = (arr[8] & 0x3f) | 0x80;
    return 'shopify-' + Array.from(arr, function (b, i) {
      var hex = b.toString(16).padStart(2, '0');
      return [4, 6, 8, 10].indexOf(i) !== -1 ? '-' + hex : hex;
    }).join('');
  }

  // Pure: decides and performs storage given the consent answer, so it can be
  // unit-tested with fake storages.
  function resolveUserID(persistAllowed, local, session, makeID) {
    function get(s) { try { return s && s.getItem(UID_KEY); } catch (e) { return null; } }
    function set(s, v) { try { s && s.setItem(UID_KEY, v); return true; } catch (e) { return false; } }
    function del(s) { try { s && s.removeItem(UID_KEY); } catch (e) {} }

    if (persistAllowed) {
      var id = get(local) || get(session) || makeID();
      set(local, id);
      del(session);
      return id;
    }
    del(local);
    var sid = get(session) || makeID();
    set(session, sid);
    return sid;
  }

  // Shopify's Customer Privacy API (fed by the store's cookie banner). Absent
  // or erroring API means no consent signal, which means no persistence.
  function preferencesAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.preferencesProcessingAllowed === 'function' &&
        cp.preferencesProcessingAllowed());
    } catch (e) { return false; }
  }

  // Load the Customer Privacy API if the theme has not, then answer. Never
  // waits more than a second: the chat must open even if the API is slow.
  function withConsent(cb) {
    var done = false;
    function finish() { if (!done) { done = true; cb(preferencesAllowed()); } }
    try {
      var s = window.Shopify;
      if (s && !s.customerPrivacy && typeof s.loadFeatures === 'function') {
        s.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish);
        setTimeout(finish, 1000);
        return;
      }
    } catch (e) {}
    finish();
  }

  function getUserID(persistAllowed) {
    var local = null, session = null;
    try { local = window.localStorage; } catch (e) {}
    try { session = window.sessionStorage; } catch (e) {}
    var memo = null;
    return resolveUserID(persistAllowed, local, session, function () {
      return memo || (memo = newUserID());
    });
  }

  // --- Voiceflow /v4 web chat API ---
  // Voiceflow support (2026-10): a conversation is a session. The Remi session
  // server starts it with the project key, which must not be in the page, and
  // pins it to Main (or Development for the dev store). The session key it
  // returns works for that one conversation only, so the chat keeps it with
  // its history and sends it as the Authorization header on every turn.
  function vfStartSession(sessionURL, uid) {
    return fetch(sessionURL, {
      method: 'POST',
      // text/plain keeps this a "simple" cross-origin request: no CORS
      // preflight, so one round trip less before the greeting.
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ userID: uid })
    }).then(function (r) {
      if (!r.ok) throw new Error('session ' + r.status);
      return r.json();
    }).then(function (j) {
      if (!j || !j.sessionKey) throw new Error('session key missing');
      return j.sessionKey;
    });
  }

  // Variables are applied before the turn runs, so the launch carries the
  // page the chat was opened on. /v4 only streams text with completionEvents
  // in each turn's config (found 2026-10-20; snake_case or a query parameter
  // are ignored and the reply arrives whole).
  function turnBody(action, vars) {
    var body = { action: action, config: { tts: false, stripSSML: true, completionEvents: true } };
    if (vars) body.variables = vars;
    return body;
  }

  // One turn, streamed: onTrace gets each trace as it arrives and the promise
  // resolves with all of them once Voiceflow sends `end`. A session key
  // Voiceflow no longer accepts fails with `expired` set: 401 for a bad key,
  // 404 for one that timed out or was replaced by a newer session.
  function vfTurn(sessionKey, action, vars, signal, onTrace) {
    return fetch(VF_URL + '/v4/interact/stream', {
      method: 'POST',
      signal: signal,
      headers: {
        'Authorization': sessionKey,
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify(turnBody(action, vars))
    }).then(function (r) {
      if (!r.ok || !r.body || !r.body.getReader || typeof TextDecoder === 'undefined') {
        var err = new Error('VF stream ' + r.status);
        if (r.status === 401 || r.status === 404) err.expired = true;
        throw err;
      }
      var reader = r.body.getReader();
      var dec = new TextDecoder();
      var buf = '';
      var traces = [];
      function pump() {
        return reader.read().then(function (chunk) {
          if (chunk.done) return traces;
          var parsed = parseSSE(buf + dec.decode(chunk.value, { stream: true }));
          buf = parsed.rest;
          var ended = false;
          parsed.events.forEach(function (e) {
            if (e.event === 'end') ended = true;
            else if (!ended && e.data && e.data.type) { traces.push(e.data); onTrace(e.data); }
          });
          if (ended) { reader.cancel().catch(function () {}); return traces; }
          return pump();
        });
      }
      return pump();
    });
  }

  // --- Reply watch ---
  // Voiceflow has twice (2026-10-10) finished a reply in seconds and delivered it
  // to the browser 4-5 minutes later. The customer must never wait that long:
  // after slowMs the widget says it is still working, after giveUpMs it aborts
  // the request and offers to try again. A full routine legitimately takes about
  // a minute, so the give-up limit sits well above that. `timers` is injectable
  // for tests.
  var SLOW_MS = 20000;
  var GIVE_UP_MS = 90000;
  var LAUNCH_GIVE_UP_MS = 30000;

  function watchReply(slowMs, giveUpMs, onSlow, timers) {
    // Wrapped, not `{ set: setTimeout }`: browsers throw "Illegal invocation" when
    // setTimeout is called as a method of another object (live outage 2026-10-10).
    timers = timers || {
      set: function (fn, ms) { return setTimeout(fn, ms); },
      clear: function (id) { clearTimeout(id); }
    };
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var slow = timers.set(onSlow, slowMs);
    var stop = timers.set(function () { if (ctrl) ctrl.abort(); }, giveUpMs);
    return {
      signal: ctrl ? ctrl.signal : undefined,
      done: function () { timers.clear(slow); timers.clear(stop); }
    };
  }

  function isAbort(e) { return !!e && e.name === 'AbortError'; }

  // --- One turn on the chat's session ---
  // The first turn starts the session; a message typed while it is starting
  // waits for the same one. A key Voiceflow refuses gets one fresh session and
  // the turn is sent again. A new session is a new conversation (the agent has
  // forgotten the chat, seen 2026-10-20), so it always gets the page variables.
  // `api` is { start(uid), turn(key, action, vars, signal, onTrace), vars() },
  // injectable for tests; chat.sk is saved with the history.
  function runTurn(api, chat, action, vars, signal, onTrace) {
    function session(renew) {
      if (renew || !chat.session) {
        var p = chat.sk && !renew
          ? Promise.resolve(chat.sk)
          : api.start(chat.uid).then(function (key) { chat.sk = key; return key; });
        // a failed start is tried again on the next turn
        p.catch(function () { if (chat.session === p) chat.session = null; });
        chat.session = p;
      }
      return chat.session;
    }
    function send(key, isNew) {
      return api.turn(key, action, isNew ? (vars || api.vars()) : vars, signal, onTrace);
    }
    var isNew = !chat.session && !chat.sk;
    return session(false).then(function (key) {
      return send(key, isNew).catch(function (e) {
        if (!e || !e.expired) throw e;
        return session(true).then(function (fresh) { return send(fresh, true); });
      });
    });
  }

  // Map a browser locale (e.g. "da-DK", "fr-BE") to one of the agent's
  // supported language codes. Mirrors the Welcome playbook's locale table so
  // downstream playbooks (which read {detected_language}) speak the right
  // language from the very first turn.
  function mapLanguage(loc) {
    if (!loc) return '';
    var base = String(loc).toLowerCase().split('-')[0];
    var map = {
      fr: 'fr', de: 'de', nl: 'nl', it: 'it', es: 'es', pt: 'pt',
      sv: 'sv', da: 'da', nb: 'no', nn: 'no', no: 'no', en: 'en'
    };
    return map[base] || 'en';
  }

  // --- Storefront enrichment + cart helpers ─────────────────────────────
  // The widget runs on the Shopify storefront, so these relative endpoints
  // are same-origin and need no auth: `/products/{handle}.js` (product data),
  // `/cart/add.js` (add), `/cart.js` (read count). On the password-protected
  // dev store the customer's browser session carries the storefront cookie,
  // so these succeed in-browser even though an anonymous request 401s.

  // Localized button strings, keyed by the same language codes mapLanguage
  // emits. Falls back to English for any missing key/locale.
  var I18N = {
    en: { add: 'Add to cart',            oos: 'Out of stock', added: '✓ Added — View cart',          routine: 'Add whole routine to cart',       adding: 'Adding…',        err: 'Could not add — try again', ai: 'You\'re chatting with an AI assistant. Answers may contain mistakes.',
          slow: 'Still working on it — detailed answers can take up to a minute.', late: 'Sorry, this is taking longer than usual.', retry: 'Try again', fail: 'I\'m having trouble connecting right now. Please try again in a moment.', hi: 'Hi! I\'m your ELEVEN Australia AI hair advisor. How can I help you today?', pick: 'Please choose one of these options, or type your answer:', again: 'Sorry, I didn\'t manage to answer that. Could you say it again?' },
    da: { add: 'Læg i kurv',        oos: 'Udsolgt',      added: '✓ Tilføjet — Se kurv',    routine: 'Læg hele rutinen i kurven',   adding: 'Tilføjer…', err: 'Kunne ikke tilføje — prøv igen', ai: 'Du chatter med en AI-assistent. Svarene kan indeholde fejl.',
          slow: 'Arbejder stadig på det — udførlige svar kan tage op til et minut.', late: 'Beklager, det tager længere tid end normalt.', retry: 'Prøv igen', fail: 'Jeg har problemer med forbindelsen lige nu. Prøv igen om lidt.', hi: 'Hej! Jeg er din AI-hårrådgiver fra ELEVEN Australia. Hvordan kan jeg hjælpe dig i dag?', pick: 'Vælg en af mulighederne, eller skriv dit svar:', again: 'Beklager, det lykkedes mig ikke at svare. Vil du skrive det igen?' },
    fr: { add: 'Ajouter au panier',      oos: 'Épuisé', added: '✓ Ajouté — Voir le panier', routine: 'Ajouter toute la routine au panier', adding: 'Ajout…',    err: 'Échec — réessayez', ai: 'Vous discutez avec un assistant IA. Les réponses peuvent contenir des erreurs.',
          slow: 'Je travaille encore dessus — une réponse détaillée peut prendre jusqu\'à une minute.', late: 'Désolé, cela prend plus de temps que d\'habitude.', retry: 'Réessayer', fail: 'J\'ai du mal à me connecter pour le moment. Veuillez réessayer dans un instant.', hi: 'Bonjour ! Je suis votre conseiller capillaire IA d\'ELEVEN Australia. Comment puis-je vous aider aujourd\'hui ?', pick: 'Choisissez l\'une de ces options ou écrivez votre réponse :', again: 'Désolé, je n\'ai pas réussi à répondre. Pouvez-vous reformuler votre message ?' },
    de: { add: 'In den Warenkorb',       oos: 'Ausverkauft',  added: '✓ Hinzugefügt — Warenkorb', routine: 'Ganze Routine in den Warenkorb',  adding: 'Wird hinzugefügt…', err: 'Fehlgeschlagen — erneut versuchen', ai: 'Sie chatten mit einem KI-Assistenten. Antworten können Fehler enthalten.',
          slow: 'Einen Moment noch — ausführliche Antworten können bis zu einer Minute dauern.', late: 'Entschuldigung, das dauert länger als üblich.', retry: 'Erneut versuchen', fail: 'Ich habe gerade Verbindungsprobleme. Bitte versuchen Sie es gleich noch einmal.', hi: 'Hallo! Ich bin Ihr KI-Haarberater von ELEVEN Australia. Wie kann ich Ihnen heute helfen?', pick: 'Bitte wählen Sie eine dieser Optionen oder schreiben Sie Ihre Antwort:', again: 'Entschuldigung, das konnte ich nicht beantworten. Könnten Sie es noch einmal schreiben?' },
    nl: { add: 'In winkelwagen',         oos: 'Uitverkocht',  added: '✓ Toegevoegd — Bekijk winkelwagen', routine: 'Hele routine toevoegen',      adding: 'Toevoegen…', err: 'Mislukt — probeer opnieuw', ai: 'Je chat met een AI-assistent. Antwoorden kunnen fouten bevatten.',
          slow: 'Nog even geduld — uitgebreide antwoorden kunnen tot een minuut duren.', late: 'Sorry, dit duurt langer dan normaal.', retry: 'Opnieuw proberen', fail: 'Ik heb nu verbindingsproblemen. Probeer het zo meteen opnieuw.', hi: 'Hoi! Ik ben je AI-haaradviseur van ELEVEN Australia. Waarmee kan ik je vandaag helpen?', pick: 'Kies een van deze opties of typ je antwoord:', again: 'Sorry, dat lukte me niet. Kun je het nog een keer typen?' },
    it: { add: 'Aggiungi al carrello',   oos: 'Esaurito',     added: '✓ Aggiunto — Vedi carrello',    routine: 'Aggiungi tutta la routine',        adding: 'Aggiunta…',  err: 'Errore — riprova', ai: 'Stai chattando con un assistente IA. Le risposte possono contenere errori.',
          slow: 'Ci sto ancora lavorando — una risposta dettagliata può richiedere fino a un minuto.', late: 'Spiacenti, ci sta volendo più del solito.', retry: 'Riprova', fail: 'Ho problemi di connessione in questo momento. Riprova tra poco.', hi: 'Ciao! Sono il tuo consulente per capelli IA di ELEVEN Australia. Come posso aiutarti oggi?', pick: 'Scegli una di queste opzioni o scrivi la tua risposta:', again: 'Scusa, non sono riuscito a rispondere. Puoi riscriverlo?' },
    es: { add: 'Añadir al carrito', oos: 'Agotado',      added: '✓ Añadido — Ver carrito',  routine: 'Añadir toda la rutina',       adding: 'Añadiendo…', err: 'Error — inténtalo de nuevo', ai: 'Estás chateando con un asistente de IA. Las respuestas pueden contener errores.',
          slow: 'Sigo con ello — una respuesta detallada puede tardar hasta un minuto.', late: 'Lo siento, está tardando más de lo habitual.', retry: 'Reintentar', fail: 'Ahora mismo tengo problemas de conexión. Inténtalo de nuevo en un momento.', hi: '¡Hola! Soy tu asesor capilar con IA de ELEVEN Australia. ¿En qué puedo ayudarte hoy?', pick: 'Elige una de estas opciones o escribe tu respuesta:', again: 'Lo siento, no he podido responder. ¿Puedes repetirlo?' },
    pt: { add: 'Adicionar ao carrinho',  oos: 'Esgotado',     added: '✓ Adicionado — Ver carrinho',  routine: 'Adicionar toda a rotina',          adding: 'A adicionar…', err: 'Falhou — tente novamente', ai: 'Está a conversar com um assistente de IA. As respostas podem conter erros.',
          slow: 'Ainda estou a trabalhar nisso — uma resposta detalhada pode demorar até um minuto.', late: 'Desculpe, está a demorar mais do que o habitual.', retry: 'Tentar novamente', fail: 'Estou com problemas de ligação neste momento. Tente novamente daqui a pouco.', hi: 'Olá! Sou o seu consultor capilar de IA da ELEVEN Australia. Como posso ajudar hoje?', pick: 'Escolha uma destas opções ou escreva a sua resposta:', again: 'Desculpe, não consegui responder. Pode escrever novamente?' },
    sv: { add: 'Lägg i varukorg',   oos: 'Slutsåld', added: '✓ Tillagd — Visa varukorg',    routine: 'Lägg hela rutinen i varukorgen', adding: 'Lägger till…', err: 'Misslyckades — försök igen', ai: 'Du chattar med en AI-assistent. Svaren kan innehålla fel.',
          slow: 'Jobbar fortfarande på det — utförliga svar kan ta upp till en minut.', late: 'Förlåt, det tar längre tid än vanligt.', retry: 'Försök igen', fail: 'Jag har problem med anslutningen just nu. Försök igen om en stund.', hi: 'Hej! Jag är din AI-hårrådgivare från ELEVEN Australia. Hur kan jag hjälpa dig i dag?', pick: 'Välj ett av alternativen eller skriv ditt svar:', again: 'Förlåt, jag lyckades inte svara. Kan du skriva det igen?' },
    no: { add: 'Legg i handlekurv',      oos: 'Utsolgt',      added: '✓ Lagt til — Se handlekurv',   routine: 'Legg hele rutinen i handlekurven', adding: 'Legger til…', err: 'Mislyktes — prøv igjen', ai: 'Du chatter med en KI-assistent. Svarene kan inneholde feil.',
          slow: 'Jobber fortsatt med det — utfyllende svar kan ta opptil ett minutt.', late: 'Beklager, dette tar lengre tid enn vanlig.', retry: 'Prøv igjen', fail: 'Jeg har problemer med tilkoblingen akkurat nå. Prøv igjen om litt.', hi: 'Hei! Jeg er din KI-hårrådgiver fra ELEVEN Australia. Hvordan kan jeg hjelpe deg i dag?', pick: 'Velg ett av alternativene, eller skriv svaret ditt:', again: 'Beklager, jeg klarte ikke å svare. Kan du skrive det igjen?' }
  };

  // Product-page starters: the button label, and the sentence it sends, which
  // names the product so the agent knows what "it" is (no agent change needed).
  var STARTERS = {
    en: [{ label: 'Is this right for my hair?', send: 'Is {title} right for my hair?' },
         { label: 'How do I use it?', send: 'How do I use {title}?' },
         { label: 'What goes well with it?', send: 'What goes well with {title}?' }],
    da: [{ label: 'Passer det til mit hår?', send: 'Passer {title} til mit hår?' },
         { label: 'Hvordan bruger jeg det?', send: 'Hvordan bruger jeg {title}?' },
         { label: 'Hvad passer godt til det?', send: 'Hvad passer godt sammen med {title}?' }],
    fr: [{ label: 'Est-ce adapté à mes cheveux ?', send: 'Le produit {title} est-il adapté à mes cheveux ?' },
         { label: 'Comment l\'utiliser ?', send: 'Comment utiliser {title} ?' },
         { label: 'Avec quoi l\'associer ?', send: 'Avec quoi associer {title} ?' }],
    de: [{ label: 'Passt das zu meinem Haar?', send: 'Passt {title} zu meinem Haar?' },
         { label: 'Wie wende ich es an?', send: 'Wie wende ich {title} an?' },
         { label: 'Was passt gut dazu?', send: 'Was passt gut zu {title}?' }],
    nl: [{ label: 'Past dit bij mijn haar?', send: 'Past {title} bij mijn haar?' },
         { label: 'Hoe gebruik ik het?', send: 'Hoe gebruik ik {title}?' },
         { label: 'Wat past er goed bij?', send: 'Wat past goed bij {title}?' }],
    it: [{ label: 'È adatto ai miei capelli?', send: '{title} è adatto ai miei capelli?' },
         { label: 'Come si usa?', send: 'Come si usa {title}?' },
         { label: 'Con cosa abbinarlo?', send: 'Con cosa abbinare {title}?' }],
    es: [{ label: '¿Es adecuado para mi pelo?', send: '¿{title} es adecuado para mi pelo?' },
         { label: '¿Cómo se usa?', send: '¿Cómo se usa {title}?' },
         { label: '¿Con qué combinarlo?', send: '¿Con qué combino {title}?' }],
    pt: [{ label: 'É indicado para o meu cabelo?', send: '{title} é indicado para o meu cabelo?' },
         { label: 'Como se usa?', send: 'Como se usa {title}?' },
         { label: 'Com o que combinar?', send: 'Com o que combinar {title}?' }],
    sv: [{ label: 'Passar det mitt hår?', send: 'Passar {title} mitt hår?' },
         { label: 'Hur använder jag det?', send: 'Hur använder jag {title}?' },
         { label: 'Vad passar bra till det?', send: 'Vad passar bra till {title}?' }],
    no: [{ label: 'Passer det til håret mitt?', send: 'Passer {title} til håret mitt?' },
         { label: 'Hvordan bruker jeg det?', send: 'Hvordan bruker jeg {title}?' },
         { label: 'Hva passer godt til det?', send: 'Hva passer godt til {title}?' }]
  };

  // Collection and concern-page starters: {name} is the collection or page title.
  var PICK_STARTERS = {
    en: [{ label: 'Help me choose', send: 'Help me choose from {name}.' },
         { label: 'What\'s best for my hair?', send: 'Which {name} product is best for my hair?' },
         { label: 'Build me a routine', send: 'Build me a routine with {name}.' }],
    da: [{ label: 'Hjælp mig med at vælge', send: 'Hjælp mig med at vælge fra {name}.' },
         { label: 'Hvad er bedst til mit hår?', send: 'Hvilket {name}-produkt er bedst til mit hår?' },
         { label: 'Lav en rutine til mig', send: 'Lav en rutine til mig med {name}.' }],
    fr: [{ label: 'Aidez-moi à choisir', send: 'Aidez-moi à choisir dans {name}.' },
         { label: 'Le meilleur pour mes cheveux ?', send: 'Quel produit {name} est le meilleur pour mes cheveux ?' },
         { label: 'Créez-moi une routine', send: 'Créez-moi une routine avec {name}.' }],
    de: [{ label: 'Hilf mir bei der Auswahl', send: 'Hilf mir bei der Auswahl aus {name}.' },
         { label: 'Was passt am besten zu mir?', send: 'Welches {name}-Produkt passt am besten zu meinem Haar?' },
         { label: 'Stell mir eine Routine zusammen', send: 'Stell mir eine Routine mit {name} zusammen.' }],
    nl: [{ label: 'Help me kiezen', send: 'Help me kiezen uit {name}.' },
         { label: 'Wat is het beste voor mijn haar?', send: 'Welk {name}-product is het beste voor mijn haar?' },
         { label: 'Stel een routine voor me samen', send: 'Stel een routine met {name} voor me samen.' }],
    it: [{ label: 'Aiutami a scegliere', send: 'Aiutami a scegliere tra {name}.' },
         { label: 'Il migliore per i miei capelli?', send: 'Quale prodotto {name} è il migliore per i miei capelli?' },
         { label: 'Creami una routine', send: 'Creami una routine con {name}.' }],
    es: [{ label: 'Ayúdame a elegir', send: 'Ayúdame a elegir entre {name}.' },
         { label: '¿Cuál es mejor para mi pelo?', send: '¿Qué producto de {name} es mejor para mi pelo?' },
         { label: 'Créame una rutina', send: 'Créame una rutina con {name}.' }],
    pt: [{ label: 'Ajude-me a escolher', send: 'Ajude-me a escolher em {name}.' },
         { label: 'Qual é o melhor para mim?', send: 'Que produto {name} é o melhor para o meu cabelo?' },
         { label: 'Crie-me uma rotina', send: 'Crie-me uma rotina com {name}.' }],
    sv: [{ label: 'Hjälp mig att välja', send: 'Hjälp mig att välja bland {name}.' },
         { label: 'Vad passar mitt hår bäst?', send: 'Vilken {name}-produkt passar mitt hår bäst?' },
         { label: 'Gör en rutin åt mig', send: 'Gör en rutin åt mig med {name}.' }],
    no: [{ label: 'Hjelp meg å velge', send: 'Hjelp meg å velge fra {name}.' },
         { label: 'Hva passer best til meg?', send: 'Hvilket {name}-produkt passer best til håret mitt?' },
         { label: 'Lag en rutine til meg', send: 'Lag en rutine til meg med {name}.' }]
  };

  // Homepage concern buttons (data-remi-ask="frizz" ...): label, and the
  // question sent for the shopper when it is tapped.
  var CONCERNS = {
    en: { frizz: { label: 'Frizz', send: 'I have frizzy hair. What do you recommend?' },
         dry: { label: 'Dryness', send: 'My hair is dry. What do you recommend?' },
         damage: { label: 'Damage', send: 'My hair is damaged. What do you recommend?' },
         volume: { label: 'Volume', send: 'My hair is flat and needs volume. What do you recommend?' },
         colour: { label: 'Colour & blonde', send: 'I have coloured or blonde hair. What do you recommend?' },
         curls: { label: 'Curls', send: 'I have curly hair. What do you recommend?' } },
    da: { frizz: { label: 'Kruset hår', send: 'Mit hår er kruset. Hvad anbefaler I?' },
         dry: { label: 'Tørt hår', send: 'Mit hår er tørt. Hvad anbefaler I?' },
         damage: { label: 'Skadet hår', send: 'Mit hår er skadet. Hvad anbefaler I?' },
         volume: { label: 'Volumen', send: 'Mit hår er fladt og mangler volumen. Hvad anbefaler I?' },
         colour: { label: 'Farve & blond', send: 'Jeg har farvet eller blondt hår. Hvad anbefaler I?' },
         curls: { label: 'Krøller', send: 'Jeg har krøllet hår. Hvad anbefaler I?' } },
    fr: { frizz: { label: 'Frisottis', send: 'J\'ai des frisottis. Que me conseillez-vous ?' },
         dry: { label: 'Sécheresse', send: 'Mes cheveux sont secs. Que me conseillez-vous ?' },
         damage: { label: 'Cheveux abîmés', send: 'Mes cheveux sont abîmés. Que me conseillez-vous ?' },
         volume: { label: 'Volume', send: 'Mes cheveux sont plats et manquent de volume. Que me conseillez-vous ?' },
         colour: { label: 'Couleur & blond', send: 'J\'ai les cheveux colorés ou blonds. Que me conseillez-vous ?' },
         curls: { label: 'Boucles', send: 'J\'ai les cheveux bouclés. Que me conseillez-vous ?' } },
    de: { frizz: { label: 'Frizz', send: 'Ich habe krauses Haar. Was empfehlen Sie?' },
         dry: { label: 'Trockenheit', send: 'Mein Haar ist trocken. Was empfehlen Sie?' },
         damage: { label: 'Schäden', send: 'Mein Haar ist geschädigt. Was empfehlen Sie?' },
         volume: { label: 'Volumen', send: 'Mein Haar ist platt und braucht Volumen. Was empfehlen Sie?' },
         colour: { label: 'Farbe & Blond', send: 'Ich habe gefärbtes oder blondes Haar. Was empfehlen Sie?' },
         curls: { label: 'Locken', send: 'Ich habe lockiges Haar. Was empfehlen Sie?' } },
    nl: { frizz: { label: 'Pluis', send: 'Ik heb pluizig haar. Wat raden jullie aan?' },
         dry: { label: 'Droog haar', send: 'Mijn haar is droog. Wat raden jullie aan?' },
         damage: { label: 'Beschadigd haar', send: 'Mijn haar is beschadigd. Wat raden jullie aan?' },
         volume: { label: 'Volume', send: 'Mijn haar is plat en heeft volume nodig. Wat raden jullie aan?' },
         colour: { label: 'Kleur & blond', send: 'Ik heb gekleurd of blond haar. Wat raden jullie aan?' },
         curls: { label: 'Krullen', send: 'Ik heb krullend haar. Wat raden jullie aan?' } },
    it: { frizz: { label: 'Crespo', send: 'Ho i capelli crespi. Cosa mi consigliate?' },
         dry: { label: 'Secchezza', send: 'Ho i capelli secchi. Cosa mi consigliate?' },
         damage: { label: 'Capelli danneggiati', send: 'Ho i capelli danneggiati. Cosa mi consigliate?' },
         volume: { label: 'Volume', send: 'Ho i capelli piatti e senza volume. Cosa mi consigliate?' },
         colour: { label: 'Colore & biondo', send: 'Ho i capelli colorati o biondi. Cosa mi consigliate?' },
         curls: { label: 'Ricci', send: 'Ho i capelli ricci. Cosa mi consigliate?' } },
    es: { frizz: { label: 'Encrespado', send: 'Tengo el pelo encrespado. ¿Qué me recomendáis?' },
         dry: { label: 'Sequedad', send: 'Tengo el pelo seco. ¿Qué me recomendáis?' },
         damage: { label: 'Pelo dañado', send: 'Tengo el pelo dañado. ¿Qué me recomendáis?' },
         volume: { label: 'Volumen', send: 'Tengo el pelo sin volumen. ¿Qué me recomendáis?' },
         colour: { label: 'Color & rubio', send: 'Tengo el pelo teñido o rubio. ¿Qué me recomendáis?' },
         curls: { label: 'Rizos', send: 'Tengo el pelo rizado. ¿Qué me recomendáis?' } },
    pt: { frizz: { label: 'Frizz', send: 'Tenho o cabelo com frizz. O que recomendam?' },
         dry: { label: 'Secura', send: 'Tenho o cabelo seco. O que recomendam?' },
         damage: { label: 'Cabelo danificado', send: 'Tenho o cabelo danificado. O que recomendam?' },
         volume: { label: 'Volume', send: 'Tenho o cabelo sem volume. O que recomendam?' },
         colour: { label: 'Cor & loiro', send: 'Tenho o cabelo pintado ou loiro. O que recomendam?' },
         curls: { label: 'Caracóis', send: 'Tenho o cabelo encaracolado. O que recomendam?' } },
    sv: { frizz: { label: 'Frizz', send: 'Jag har frissigt hår. Vad rekommenderar ni?' },
         dry: { label: 'Torrt hår', send: 'Mitt hår är torrt. Vad rekommenderar ni?' },
         damage: { label: 'Skadat hår', send: 'Mitt hår är skadat. Vad rekommenderar ni?' },
         volume: { label: 'Volym', send: 'Mitt hår är platt och behöver volym. Vad rekommenderar ni?' },
         colour: { label: 'Färg & blont', send: 'Jag har färgat eller blont hår. Vad rekommenderar ni?' },
         curls: { label: 'Lockar', send: 'Jag har lockigt hår. Vad rekommenderar ni?' } },
    no: { frizz: { label: 'Kruset hår', send: 'Jeg har kruset hår. Hva anbefaler dere?' },
         dry: { label: 'Tørt hår', send: 'Håret mitt er tørt. Hva anbefaler dere?' },
         damage: { label: 'Skadet hår', send: 'Håret mitt er skadet. Hva anbefaler dere?' },
         volume: { label: 'Volum', send: 'Håret mitt er flatt og trenger volum. Hva anbefaler dere?' },
         colour: { label: 'Farge & blond', send: 'Jeg har farget eller blondt hår. Hva anbefaler dere?' },
         curls: { label: 'Krøller', send: 'Jeg har krøllete hår. Hva anbefaler dere?' } }
  };

  // One question waiting for the chat to be ready (the launch has to run
  // first). A second tap before it is sent is ignored, so nothing goes twice.
  function askQueue() {
    var pending = null;
    return {
      ask: function (text) { if (pending) return false; pending = text; return true; },
      take: function () { var p = pending; pending = null; return p; }
    };
  }

  // Write text only when it differs: an identical write is still a DOM change,
  // and a relabel driven by a MutationObserver once fed itself forever and froze
  // collection pages (test theme, 2026-10-20).
  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  // Homepage hero on screen (any part of it in the viewport)?
  function heroInView(rect, viewportH) { return rect.bottom > 0 && rect.top < viewportH; }

  // The Welcome playbook's "find products" button (Button A) in every language.
  // The greeting buttons come from the model, so this one is found by wording,
  // never by position: the order button must survive whatever order they arrive in.
  var FIND_LABELS = ['Help me find products', 'M\'aider à trouver des produits', 'Produkte finden',
    'Help me producten vinden', 'Aiutami a trovare prodotti', 'Ayúdame a encontrar productos',
    'Ajude-me a encontrar produtos', 'Hjälp mig hitta produkter', 'Hjælp mig med at finde produkter',
    'Hjelp meg å finne produkter'];
  function isFindButton(btn) {
    var name = String((btn && btn.name) || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return FIND_LABELS.some(function (l) { return l.toLowerCase() === name; });
  }

  // EU AI Act Art. 50 disclosure, shown by the widget itself as the first line
  // of every conversation: the model's greeting carries it too, but a greeting
  // can come back as buttons only (one in three on 2026-10-09).
  function aiNotice(lang) { return (I18N[lang] || I18N.en).ai; }

  // The greeting model sometimes sends only the buttons (Flash, 2026-10-10). The
  // widget then writes the greeting itself, above them, in the visitor's language.
  function withGreeting(traces, lang) {
    traces = traces || [];
    var hasText = traces.some(function (t) { return t && t.type === 'text' && t.payload && String(t.payload.message || '').trim(); });
    if (hasText) return traces;
    return [{ type: 'text', payload: { message: (I18N[lang] || I18N.en).hi } }].concat(traces);
  }

  // Most EU product titles are stored in capitals, which in the customer's own
  // message bubble reads as shouting. Words in capitals become normal case
  // ("ANTI-FRIZZ" -> "Anti-Frizz", "85G" -> "85g", "AND" -> "and"); words that
  // already have lower case, and single letters like "I", are kept.
  function readableTitle(title) {
    return String(title).split(' ').map(function (word, n) {
      if (n > 0 && word === 'AND') return 'and';
      return word.split('-').map(function (part) {
        if (part.length < 2 || /[a-z]/.test(part) || !/[A-Z]/.test(part)) return part;
        return part.charAt(0) + part.slice(1).toLowerCase();
      }).join('-');
    }).join(' ');
  }

  // Half the chats opened on product pages got no first message (9-20 Oct
  // 2026); those who wrote asked about the product on screen. On a product page
  // the greeting's generic "Help me find products" button becomes three starters
  // about this product, placed first. The order and language buttons stay as
  // they are. No title (landing page): unchanged.
  function withStarters(traces, lang, title) {
    return applyStarters(traces, STARTERS[lang] || STARTERS.en, '{title}', title);
  }

  // Collection and concern pages have no product: the starters name the page.
  function withPickStarters(traces, lang, name) {
    return applyStarters(traces, PICK_STARTERS[lang] || PICK_STARTERS.en, '{name}', name);
  }

  // Shared by both: put `value` (made readable) into each sentence, then swap
  // the find-products button for the starters, keeping every other button.
  function applyStarters(traces, list, key, value) {
    traces = traces || [];
    if (!value) return traces;
    value = readableTitle(value);
    var starters = list.map(function (s) {
      return { name: s.label, send: s.send.replace(key, function () { return value; }) };
    });
    var i = -1;
    traces.some(function (t, n) { if (t && t.type === 'choice') { i = n; return true; } return false; });
    if (i < 0) return traces.concat([{ type: 'choice', payload: { buttons: starters } }]);
    var old = (traces[i].payload && traces[i].payload.buttons) || [];
    var out = traces.slice();
    out[i] = { type: 'choice', payload: { buttons: starters.concat(old.filter(function (b) { return !isFindButton(b); })) } };
    return out;
  }

  // Starters belong to a greeting nobody has answered yet. Once the customer
  // has written (even while the greeting was still loading), draw it as sent.
  function startersFor(items, traces, lang, title, name) {
    if ((items || []).some(function (it) { return it.k === 'user'; })) return traces;
    return title ? withStarters(traces, lang, title) : withPickStarters(traces, lang, name);
  }

  // Same habit later in the chat: a reply that is only buttons gets a short line
  // above them. Replies with text, or with product cards, are left alone.
  function withPrompt(traces, lang) {
    traces = traces || [];
    var shows = function (type) { return traces.some(function (t) { return t && t.type === type; }); };
    var hasText = traces.some(function (t) { return t && t.type === 'text' && t.payload && String(t.payload.message || '').trim(); });
    var s = I18N[lang] || I18N.en;
    // Nothing to draw (e.g. the model spent its whole token budget and wrote
    // no text): without this the turn ends in silence.
    if (!hasText && !shows('choice') && !shows('cardV2') && !shows('carousel') && !shows('visual')) {
      return [{ type: 'text', payload: { message: s.again } }].concat(traces);
    }
    if (hasText || !shows('choice') || shows('cardV2') || shows('carousel')) return traces;
    return [{ type: 'text', payload: { message: s.pick } }].concat(traces);
  }

  // --- Streamed replies ---
  // Voiceflow's stream endpoint sends server-sent events: one trace per frame,
  // with generated text as `completion` traces (start, many content deltas,
  // end), so the reply can be shown while it is written. Payloads can carry
  // raw control characters that JSON.parse rejects, so those are dropped.
  function parseSSE(buffer) {
    var events = [];
    var frames = buffer.replace(/\r\n/g, '\n').split('\n\n');
    var rest = frames.pop();
    frames.forEach(function (f) {
      var ev = /^event: ?(.*)$/m.exec(f);
      var data = /^data: ?(.*)$/m.exec(f);
      if (!data) return;
      try {
        events.push({ event: ev ? ev[1] : 'message', data: JSON.parse(data[1].replace(/[\u0000-\u001f]/g, ' ')) });
      } catch (e) { /* a malformed frame is skipped, the rest of the reply still shows */ }
    });
    return { events: events, rest: rest };
  }

  // Completion runs become ordinary text traces, so a streamed reply can go
  // through the same checks, history and redraw as a non-streamed one.
  function foldCompletions(traces) {
    var out = [];
    var text = null;
    var flush = function () { if (text && text.trim()) out.push({ type: 'text', payload: { message: text } }); text = null; };
    (traces || []).forEach(function (t) {
      if (t && t.type === 'completion') {
        var p = t.payload || {};
        if (p.state === 'start') { flush(); text = ''; }
        else if (p.state === 'content') text = (text || '') + (p.content || '');
        else if (p.state === 'end') flush();
      } else if (t) {
        flush();
        out.push(t);
      }
    });
    flush();
    return out;
  }

  // Where to scroll while a reply grows: follow it down, but stop once its
  // first line reaches the top of the view, so a long answer is read from the
  // start instead of the customer scrolling back up to find it.
  function followTop(replyTop, scrollHeight, clientHeight, gap) {
    var bottom = Math.max(0, scrollHeight - clientHeight);
    return Math.min(bottom, Math.max(0, replyTop - (gap == null ? 12 : gap)));
  }

  // Button rows answer the question they came with. Once the customer sends
  // anything (typed or tapped) they are stale, so every row goes.
  function clearChoices(container) {
    if (!container) return;
    [].forEach.call(container.querySelectorAll('.remi-choices'), function (row) { row.remove(); });
  }

  // Conversation kept for the browser tab, so moving between product pages
  // continues the chat instead of restarting it (each page used to send a fresh
  // Voiceflow launch). sessionStorage: gone when the tab closes.
  var HISTORY_KEY = 'remi_history';
  var HISTORY_MAX = 60;
  var DRAWN = { text: 1, choice: 1, cardV2: 1, carousel: 1, visual: 1 };
  function loadHistory(store) {
    try {
      var h = JSON.parse(store.getItem(HISTORY_KEY));
      return h && h.uid && Array.isArray(h.items) ? h : null;
    } catch (e) { return null; }
  }
  function saveHistory(store, h) {
    try {
      store.setItem(HISTORY_KEY, JSON.stringify({ uid: h.uid, sk: h.sk, open: !!h.open, items: h.items.slice(-HISTORY_MAX) }));
    } catch (e) { /* full or blocked: the chat still works, it just won't follow the visitor */ }
  }
  function keepTraces(traces) {
    return (traces || []).filter(function (t) { return t && DRAWN[t.type]; })
      .map(function (t) { return { type: t.type, payload: t.payload }; });
  }
  // Old answer buttons must not come back: only the very last reply keeps them.
  function replayItems(items) {
    var last = items.length - 1;
    return items.map(function (it, i) {
      if (it.k !== 'traces' || i === last) return it;
      return { k: 'traces', traces: it.traces.filter(function (t) { return t.type !== 'choice'; }) };
    });
  }
  // History keeps the greeting as Voiceflow sent it. Redrawn on another product
  // page before the customer wrote anything, it gets that page's starters.
  function startersOnRestore(items, lang, title, name) {
    var last = -1;
    items.forEach(function (it, i) { if (it.k === 'traces') last = i; });
    if (last < 0) return items;
    var traces = startersFor(items, items[last].traces, lang, title, name);
    if (traces === items[last].traces) return items;
    var out = items.slice();
    out[last] = { k: 'traces', traces: traces };
    return out;
  }

  // How far above the viewport bottom the launcher must sit to stay `gap` px
  // clear of every visible bar it would overlap (0 = no bar in the way). The
  // product page's sticky add-to-cart bar hides by sliding below the viewport,
  // so its rect top is the signal; an empty rect is display:none.
  function avoidLift(launcher, rects, viewportH, gap) {
    var lift = 0;
    rects.forEach(function (r) {
      if (!r.width || !r.height || r.top >= viewportH) return;
      if (r.right <= launcher.left || r.left >= launcher.right) return;
      lift = Math.max(lift, viewportH - r.top + gap);
    });
    return lift;
  }

  // EUR, formatted per the visitor's browser locale (€25,00 vs €25.00).
  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(navigator.language || 'en-IE',
        { style: 'currency', currency: 'EUR' }).format(cents / 100);
    } catch (e) {
      return '€' + (cents / 100).toFixed(2);
    }
  }

  // Shopify CDN images come back protocol-relative (//cdn...). Force https.
  // We deliberately do NOT append a `?width=`/`&width=` sizing param: the CDN
  // originals are already reasonably sized and the CSS (`.remi-card__thumb`,
  // object-fit:cover) constrains the display box. The thumb is a real <img>
  // (see setThumb) so a bad URL would surface as a broken-image icon rather
  // than a silently blank card.
  function toHttps(src) {
    if (!src) return src;
    return src.indexOf('//') === 0 ? 'https:' + src : src;
  }

  // Pure image-source decision — shared by the initial render and enrichment,
  // and unit-tested in test/remi-image.test.js.
  // NEVER downgrades: a Voiceflow-provided image (from the PROD catalog the
  // recommendation is actually about) ALWAYS wins. Enrichment may only FILL a
  // gap when Voiceflow sent no image; it can never replace a good one. This is
  // the fix for blank cards — the widget enriches from the storefront it is
  // embedded on (the password-protected DEV store, a separate catalog from the
  // PROD store the Voiceflow Shopify integration is bound to), whose product
  // image may be absent or may not render, and a bad replacement used to
  // silently blank the card even when Voiceflow had sent a valid image.
  function chooseImage(vfImage, fetchedImage) {
    if (vfImage) return toHttps(vfImage);
    if (fetchedImage) return toHttps(fetchedImage);
    return '';
  }

  // Pull the product handle out of a storefront URL: /products/{handle}
  // The product-page URL on a Voiceflow card button. Carousel cards and
  // cardV2 cards nest it differently, and Voiceflow has used more than one
  // shape over time, so every reader goes through here:
  //   request.payload.actions[].payload.url   (open_url action - current)
  //   request.payload.actions[].url           (older action shape)
  //   request.payload.url / btn.url           (flat)
  // A carousel reader that only knew `actions[0].url` silently dropped every
  // URL, so carousel cards got no handle, no price and no add-to-cart.
  function buttonURL(btn) {
    if (!btn) return null;
    var p = btn.request && btn.request.payload;
    var actions = (p && p.actions) || [];
    for (var i = 0; i < actions.length; i++) {
      var a = actions[i] || {};
      var u = (a.payload && a.payload.url) || a.url;
      if (u) return u;
    }
    return (p && p.url) || btn.url || null;
  }

  function extractHandle(url) {
    if (!url) return '';
    var m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  // Default variant: first in-stock variant, else the first variant.
  function pickVariant(p) {
    var vs = (p && p.variants) || [];
    if (!vs.length) return null;
    for (var i = 0; i < vs.length; i++) if (vs[i].available) return vs[i];
    return vs[0];
  }

  // POST one or more line items in a single /cart/add.js call.
  function cartAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; }, function () { throw new Error('cart ' + r.status); });
      return r.json();
    });
  }

  // --- Conversion flags (anonymous) ---
  // Mark the cart so the order shows whether Remi was involved. Cart attribute
  // `_remi` = 'opened' (chat opened) or 'engaged' (customer sent a message or
  // tapped a button); line-item property `_remi` = 'card' on products added
  // from a Remi card. Shopify copies both onto the order. Nothing on the cart
  // identifies the chat or the visitor, so an order cannot be linked to a
  // transcript. The leading underscore hides them from the customer at checkout.
  var FLAG_KEY = '_remi';
  var FLAG_RANK = { opened: 1, engaged: 2 };

  // The level to write, or null when the cart already has this level or higher
  // (a later 'opened' must never downgrade an 'engaged' cart).
  function flagUpgrade(current, wanted) {
    return (FLAG_RANK[wanted] || 0) > (FLAG_RANK[current] || 0) ? wanted : null;
  }

  // Copy of /cart/add.js line items with the Remi card property added.
  function tagItems(items) {
    return items.map(function (it) {
      var out = {}, k, props = {};
      for (k in it) out[k] = it[k];
      for (k in (it.properties || {})) props[k] = it.properties[k];
      props[FLAG_KEY] = 'card';
      out.properties = props;
      return out;
    });
  }

  // Writes run one after another, so 'opened' and 'engaged' fired close
  // together can't land out of order. Failures are ignored: the flag is
  // for reporting and must never get in the customer's way.
  var flagQueue = Promise.resolve();
  function flagCart(level) {
    flagQueue = flagQueue.then(function () {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var next = flagUpgrade(cart.attributes && cart.attributes[FLAG_KEY], level);
          if (!next) return;
          var attrs = {};
          attrs[FLAG_KEY] = next;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: attrs })
          });
        });
    }).catch(function () { /* reporting only */ });
    return flagQueue;
  }

  // Refresh the theme's cart UI after an add. Uses the theme's own mechanism
  // where possible (this store exposes a global `ajaxCart.load()` that rebuilds
  // the drawer + count), and always updates the header count bubble directly
  // as a belt-and-braces fallback. Also emits a `cart:refresh` event.
  function refreshCartUI() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = cart.item_count;
        document.querySelectorAll('.cart-count-bubble, .cart_qty_cls').forEach(function (node) {
          var span = node.querySelector('[aria-hidden="true"]');
          if (span) span.textContent = n; else node.textContent = n;
          node.classList.remove('hidden-count');
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
      })
      .catch(function () { /* count refresh is best-effort */ });
    try {
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') window.ajaxCart.load();
    } catch (e) { /* theme drawer rebuild is best-effort */ }
  }

  // --- DOM helpers ---
  function el(tag, cls) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    return e;
  }

  // Renders agent text with **bold** / *italic* / newline support — no innerHTML, no regex.exec
  function renderFormattedText(text, container) {
    // split() with a capturing group keeps the delimiters in the result array
    var mdPattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/;
    text.split('\n').forEach(function (line, li) {
      if (li > 0) container.appendChild(document.createElement('br'));
      line.split(mdPattern).forEach(function (part) {
        if (!part) return;
        if (part.slice(0, 2) === '**' && part.slice(-2) === '**') {
          var s = document.createElement('strong');
          s.textContent = part.slice(2, -2);
          container.appendChild(s);
        } else if (part[0] === '*' && part.slice(-1) === '*') {
          var e = document.createElement('em');
          e.textContent = part.slice(1, -1);
          container.appendChild(e);
        } else {
          container.appendChild(document.createTextNode(part));
        }
      });
    });
  }

  // --- Widget initialiser ---
  function initWidget(root) {
    var tpl = document.querySelector('[data-remi-tpl]');
    if (!tpl) return;

    // The session server decides Main or Development from the page's origin.
    var sessionURL = root.dataset.sessionUrl || SESSION_URL;
    var api = {
      start: function (id) { return vfStartSession(sessionURL, id); },
      turn: vfTurn,
      vars: function () { return pageVars(); }
    };

    // Debug switch: set `window.__remiDebug = true` in the browser console (then
    // trigger a new recommendation), or add a `data-debug` attribute to the
    // widget root, to log every card image decision — source (voiceflow vs
    // enrichment), the final URL, and the enrichment fetch status — to the
    // console under the "[remi]" prefix.
    var debugOn = !!(window.__remiDebug) || root.hasAttribute('data-debug');
    function dbg() {
      if (!debugOn) return;
      try { console.debug.apply(console, ['[remi]'].concat([].slice.call(arguments))); } catch (e) {}
    }

    // Created on launch, not on page load - see getUserID().
    var uid    = null;
    var handle = root.dataset.productHandle || '';
    var title  = root.dataset.productTitle  || '';
    var type   = root.dataset.productType   || '';
    var locale = root.dataset.locale        || '';
    var pageType = root.dataset.pageType    || '';
    var pageUrl  = root.dataset.pageUrl     || '';
    // 'main' on the live theme, 'unpublished' on the test theme: demo chats are
    // marked so the transcript counts can leave them out.
    var themeRole = '';
    try { themeRole = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || ''; } catch (e) {}
    // Collection or concern page: what the starters name when there is no product.
    var pickName = root.dataset.collectionTitle || (root.dataset.concern ? root.dataset.pageTitle || '' : '');
    var cta    = root.dataset.ctaText       || 'Ask our AI stylist';

    // Visitor language — drives the localized cart/routine button labels.
    // Browser language is the reliable signal (data-locale is the STORE locale).
    var browserLang = (navigator.languages && navigator.languages[0]) ||
      navigator.language || navigator.userLanguage || '';
    var lang = mapLanguage(browserLang) || 'en';

    // Inline mode: if a #remi-embed mount exists (landing page), render the
    // chat OPEN inside it with no floating launcher. Otherwise, float.
    var embed = document.getElementById('remi-embed');
    var inline = !!embed;
    var mount = inline ? embed : root;

    var panelID = 'remi-panel-' + Math.random().toString(36).slice(2, 10);
    var isOpen = false, isLaunched = false, isLoading = false;
    var isGreeted = false;   // greeting drawn (or restored): a queued question can go
    var asks = askQueue();
    var chat = null;   // { uid, sk, open, items } - this tab's conversation, see restore()
    var varsDue = false; // restored on a new page: the next turn tells the agent where we are
    var tabStore = null;
    try { tabStore = window.sessionStorage; } catch (e) { /* blocked: no history */ }

    // Inline mount: clear the snippet's static fallback content first.
    if (inline) {
      while (embed.firstChild) embed.removeChild(embed.firstChild);
    }

    // Clone static template — no innerHTML used in JS
    mount.appendChild(tpl.content.cloneNode(true));

    var w         = mount.querySelector('.remi-w');
    var toggleBtn = mount.querySelector('.remi-toggle');
    var panel     = mount.querySelector('.remi-panel');
    var closeBtn  = mount.querySelector('.remi-panel__close');
    var msgs      = mount.querySelector('.remi-msgs');
    var input     = mount.querySelector('.remi-input');
    var sendBtn   = mount.querySelector('.remi-send');

    w.classList.add(inline ? 'remi-w--inline' : 'remi-w--floating');

    // Floating launcher: rise above the page's sticky add-to-cart bar only
    // while it is on screen, and follow it as it slides in and out (0.3s).
    if (!inline) {
      var AVOID = root.dataset.avoidSelector || '#sticky-atc';
      var GAP = 8, followUntil = 0, frame = 0;
      var place = function () {
        var rects = [].map.call(document.querySelectorAll(AVOID), function (n) { return n.getBoundingClientRect(); });
        var lift = avoidLift(w.getBoundingClientRect(), rects, window.innerHeight, GAP);
        w.style.setProperty('--remi-avoid', lift + 'px');
        frame = Date.now() < followUntil ? requestAnimationFrame(place) : 0;
      };
      var follow = function () {
        followUntil = Date.now() + 450;
        if (!frame) frame = requestAnimationFrame(place);
      };
      var watch = function () {
        [].forEach.call(document.querySelectorAll(AVOID), function (n) {
          if (n.dataset.remiWatched) return;
          n.dataset.remiWatched = '1';
          new MutationObserver(follow).observe(n, { attributes: true, attributeFilter: ['class', 'style'] });
        });
        follow();
      };
      watch();
      window.addEventListener('load', watch);
      window.addEventListener('resize', follow);
    }

    // Wire IDs and set user-provided text safely via textContent
    panel.id = panelID;
    toggleBtn.setAttribute('aria-controls', panelID);
    mount.querySelector('.remi-toggle__lbl').textContent = cta;

    // While a reply is drawn, replyTop is the customer's message it answers:
    // scrolling follows the reply down but stops with that message at the top,
    // so a long answer is read from its start. Null = plain scroll to bottom.
    var replyTop = null;
    function scroll() {
      if (!replyTop || !replyTop.isConnected) { msgs.scrollTop = msgs.scrollHeight; return; }
      var top = replyTop.getBoundingClientRect().top - msgs.getBoundingClientRect().top + msgs.scrollTop;
      msgs.scrollTop = Math.max(msgs.scrollTop, followTop(top, msgs.scrollHeight, msgs.clientHeight));
    }

    function addLoader() {
      var d = el('div', 'remi-msg remi-msg--agent remi-msg--typing');
      d.appendChild(el('span'));
      d.appendChild(el('span'));
      d.appendChild(el('span'));
      msgs.appendChild(d);
      scroll();
      return d;
    }

    function renderAgentText(text) {
      var d = el('div', 'remi-msg remi-msg--agent');
      renderFormattedText(text, d);
      msgs.appendChild(d);
      scroll();
    }

    function renderUserMsg(text) {
      var d = el('div', 'remi-msg remi-msg--user');
      d.textContent = text;
      msgs.appendChild(d);
      replyTop = null;
      scroll();
    }

    function renderError(text) {
      var d = el('div', 'remi-msg remi-msg--agent');
      d.textContent = text;
      msgs.appendChild(d);
      scroll();
    }

    // Quick-reply buttons: render Voiceflow `choice` traces as tappable
    // pills. On tap we send the button's request object back through the
    // interact API and hide the whole button row. A product starter has no
    // request: it sends its full sentence (`send`) as a typed message.
    function renderChoices(buttons) {
      var wrap = el('div', 'remi-choices');
      buttons.forEach(function (btn) {
        var b = el('button', 'remi-choice');
        b.type = 'button';
        b.textContent = btn.name;
        b.addEventListener('click', function () {
          if (isLoading) return;
          wrap.remove();
          sendMessage(btn.send || btn.name, btn.request);
        });
        wrap.appendChild(b);
      });
      msgs.appendChild(wrap);
      scroll();
    }

    // Localized label lookup for the current visitor language.
    function t(key) { return (I18N[lang] || I18N.en)[key]; }

    // Add-to-cart click handler shared by single cards and the routine button.
    // `items` is the /cart/add.js line-item array; `btn` is the button to drive.
    function doAdd(items, btn) {
      if (btn.dataset.busy === '1') return;
      btn.dataset.busy = '1';
      var prev = btn.textContent;
      btn.textContent = t('adding');
      btn.disabled = true;
      cartAdd(tagItems(items))
        .then(function () {
          btn.dataset.busy = '0';
          btn.disabled = false;
          btn.textContent = t('added');
          btn.classList.add('remi-atc--added');
          // Once added, the button becomes a shortcut to the cart page.
          btn.onclick = function () { window.location.href = '/cart'; };
          refreshCartUI();
          flagCart('engaged');
        })
        .catch(function () {
          btn.dataset.busy = '0';
          btn.disabled = false;
          btn.textContent = t('err');
          setTimeout(function () { if (!btn.classList.contains('remi-atc--added')) btn.textContent = prev; }, 2500);
        });
    }

    // Normalize a cardV2 trace payload to the shared card shape.
    function cardV2Data(payload) {
      var buttons = [];
      (payload.buttons || []).forEach(function (btn) {
        buttons.push({ name: btn.name, url: buttonURL(btn) });
      });
      var handle = payload.handle || '';
      if (!handle) { for (var i = 0; i < buttons.length; i++) { if (buttons[i].url) { handle = extractHandle(buttons[i].url); if (handle) break; } } }
      return {
        title: payload.title,
        desc: (payload.description && payload.description.text) || '',
        imageUrl: payload.imageUrl, buttons: buttons, handle: handle
      };
    }

    // Normalize a legacy `carousel` trace card to the shared card shape.
    function carouselCardData(card) {
      var buttons = [];
      (card.buttons || []).forEach(function (btn) {
        buttons.push({ name: btn.name, url: buttonURL(btn) });
      });
      var handle = card.handle || '';
      if (!handle) { for (var i = 0; i < buttons.length; i++) { if (buttons[i].url) { handle = extractHandle(buttons[i].url); if (handle) break; } } }
      return {
        title: card.title,
        desc: (card.description && card.description.text) || '',
        imageUrl: card.imageUrl, buttons: buttons, handle: handle
      };
    }

    // Point the card <img> at a URL and reveal it. Using a real <img> (not a
    // background-image <div>) is deliberate and load-bearing: the theme's
    // base.css carries `div:empty { display:none }`, and `:empty` adds a
    // pseudo-class to specificity — (0,1,1) — so it OUT-SPECIFIES our
    // `.remi-card__thumb` class (0,1,0). The old empty background-image <div>
    // was therefore hidden (display:none → 0×0, painting nothing) even with a
    // perfectly valid image applied. That rule cannot match an <img> (wrong
    // element), so a real <img> is immune. See test repro measurements.
    function setThumb(thumb, url) {
      thumb.src = String(url);
      thumb.hidden = false;
    }

    // Build one product card. `ctx` is filled in with enrichment results
    // (variantId/available) so the routine button can read them at click time.
    function buildProductCard(data, ctx) {
      var c = el('div', 'remi-card');

      var thumb = el('img', 'remi-card__thumb');
      thumb.alt = data.title || 'Product image';
      thumb.setAttribute('loading', 'lazy');
      // Intrinsic size hints matching the CSS box — stable layout before load,
      // no reflow when the image arrives; object-fit:contain letterboxes the
      // full product shot inside the box.
      thumb.width = 190;
      thumb.height = 190;
      thumb.hidden = true; // stays hidden until setThumb gives it a real src
      ctx.vfImage = data.imageUrl || ''; // remembered so enrichment can never downgrade it
      var initialImg = chooseImage(ctx.vfImage, null);
      if (initialImg) {
        setThumb(thumb, initialImg);
        dbg('card', data.title || '(untitled)', '| image: voiceflow |', initialImg);
      } else {
        dbg('card', data.title || '(untitled)', '| image: NONE from voiceflow | handle:', ctx.handle || '(none)');
      }
      c.appendChild(thumb);

      var body = el('div', 'remi-card__body');

      if (data.title) {
        var ttl = el('strong', 'remi-card__title');
        ttl.textContent = data.title;
        body.appendChild(ttl);
      }

      if (data.desc) {
        var desc = el('p', 'remi-card__desc');
        desc.textContent = data.desc;
        body.appendChild(desc);
      }

      var priceEl = el('div', 'remi-card__price');
      priceEl.hidden = true;
      body.appendChild(priceEl);

      // Voiceflow buttons that carry a URL become "view product" links.
      (data.buttons || []).forEach(function (btn) {
        if (!btn.url) return;
        var link = el('a', 'remi-card__link');
        link.textContent = btn.name;
        link.href = btn.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        body.appendChild(link);
      });

      // Add-to-cart button — hidden until enrichment resolves a variant.
      var atc = el('button', 'remi-atc');
      atc.type = 'button';
      atc.hidden = true;
      body.appendChild(atc);

      c.appendChild(body);

      ctx.thumb = thumb;
      ctx.priceEl = priceEl;
      ctx.atc = atc;
      ctx.title = data.title;
      ctx.available = false;
      ctx.variantId = null;

      if (ctx.handle) enrichCard(ctx);
      return c;
    }

    // Enrich a card from the storefront: real CDN image, live price, variant,
    // add-to-cart. On any failure the card is left exactly as Voiceflow sent it.
    function enrichCard(ctx) {
      fetch('/products/' + encodeURIComponent(ctx.handle) + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (!r.ok) {
            // 401/302 on the password-protected dev store, 404 for a handle that
            // doesn't exist on this storefront, etc. Keep the Voiceflow card.
            dbg('enrich fetch failed', ctx.handle, '| HTTP', r.status, '- keeping voiceflow image');
            throw new Error('product ' + r.status);
          }
          // A redirected/locked storefront can answer 200 with an HTML page
          // instead of JSON. Reject it so we never parse HTML or blank the card.
          var ct = r.headers.get('content-type') || '';
          if (ct.indexOf('html') !== -1) {
            dbg('enrich got HTML not JSON', ctx.handle, '| content-type:', ct, '- keeping voiceflow image');
            throw new Error('non-json');
          }
          return r.json();
        })
        .then(function (p) {
          // Image: NEVER downgrade. chooseImage keeps the Voiceflow image if it
          // exists; the enrichment image only fills a gap when Voiceflow sent none.
          var fetched = p.featured_image || (p.images && p.images[0]) || '';
          if (!ctx.vfImage && fetched) {
            setThumb(ctx.thumb, chooseImage(ctx.vfImage, fetched));
            ctx.thumb.alt = p.title || ctx.title || 'Product image';
            dbg('enrich filled image gap', ctx.handle, '|', toHttps(fetched));
          } else {
            dbg('enrich', ctx.handle, '| image kept:', ctx.vfImage ? 'voiceflow (no downgrade)' : 'none available');
          }
          if (typeof p.price === 'number') {
            ctx.priceEl.textContent = (p.price_varies ? 'From ' : '') + formatMoney(p.price);
            ctx.priceEl.hidden = false;
          }
          var variant = pickVariant(p);
          ctx.available = !!(variant && variant.available);
          ctx.variantId = variant ? variant.id : null;

          var atc = ctx.atc;
          if (!ctx.available || !ctx.variantId) {
            atc.textContent = t('oos');
            atc.disabled = true;
            atc.classList.add('remi-atc--oos');
          } else {
            atc.textContent = t('add');
            atc.onclick = function () { doAdd([{ id: ctx.variantId, quantity: 1 }], atc); };
          }
          atc.hidden = false;
          scroll();
        })
        .catch(function () {
          // Any failure (fetch error, non-ok, HTML, bad JSON): the card is left
          // exactly as Voiceflow sent it — image included, no cart button.
          dbg('enrich unavailable', ctx.handle, '- card kept as voiceflow sent it');
        });
    }

    // Render a group of product cards as a carousel row. "Add whole routine to
    // cart" is appended only for a routine (2+ cards from the Routine Builder):
    // two cards from the Product Advisor are ALTERNATIVES to choose between,
    // and adding both would put two shampoos in the basket.
    function renderProductGroup(dataList, isRoutine) {
      var track = el('div', 'remi-carousel');
      var ctxs = [];
      // Single card with no URL-derived handle: fall back to the product handle
      // the widget already knows (product-page context). NEVER do this for a
      // multi-card routine carousel — each card must keep its own handle.
      var soloFallback = (dataList.length === 1 && handle) ? handle : '';
      dataList.forEach(function (data) {
        var ctx = { handle: data.handle || soloFallback };
        track.appendChild(buildProductCard(data, ctx));
        ctxs.push(ctx);
      });
      msgs.appendChild(track);

      if (isRoutine && ctxs.length >= 2) {
        var rb = el('button', 'remi-routine-add');
        rb.type = 'button';
        rb.textContent = t('routine');
        rb.onclick = function () {
          if (rb.dataset.busy === '1' || rb.classList.contains('remi-atc--added')) {
            if (rb.classList.contains('remi-atc--added')) window.location.href = '/cart';
            return;
          }
          var items = [];
          ctxs.forEach(function (cx) { if (cx.available && cx.variantId) items.push({ id: cx.variantId, quantity: 1 }); });
          if (!items.length) { rb.textContent = t('oos'); rb.disabled = true; return; }
          doAdd(items, rb);
        };
        msgs.appendChild(rb);
      }
      scroll();
    }

    // Which playbook sent a trace - Voiceflow stamps it in payload.ref.
    function isRoutineTrace(trace) {
      var ref = trace && trace.payload && trace.payload.ref;
      return !!(ref && /routine/i.test(ref.agentName || ''));
    }

    function processTraces(traces) {
      var i = 0;
      while (i < traces.length) {
        var trace = traces[i];

        if (trace.type === 'cardV2') {
          // Group consecutive cardV2 traces into one carousel row
          var group = [];
          var groupRoutine = isRoutineTrace(trace);
          while (i < traces.length && traces[i].type === 'cardV2') {
            if (traces[i].payload) group.push(cardV2Data(traces[i].payload));
            i++;
          }
          if (group.length) renderProductGroup(group, groupRoutine);
        } else {
          if (trace.type === 'text' && trace.payload && trace.payload.message) {
            renderAgentText(trace.payload.message);
          } else if (trace.type === 'choice' && trace.payload && trace.payload.buttons && trace.payload.buttons.length) {
            renderChoices(trace.payload.buttons);
          } else if (trace.type === 'carousel' && trace.payload && trace.payload.cards && trace.payload.cards.length) {
            renderProductGroup(trace.payload.cards.map(carouselCardData), isRoutineTrace(trace));
          } else if (trace.type === 'visual' && trace.payload && trace.payload.image) {
            var img = new Image();
            img.src = trace.payload.image;
            img.className = 'remi-img';
            msgs.appendChild(img);
            scroll();
          }
          i++;
        }
      }
    }

    // "Still working on it" line under the typing dots; removed with the loader.
    function addSlowNote() {
      var d = el('div', 'remi-ai-note remi-slow');
      d.textContent = t('slow');
      msgs.appendChild(d);
      scroll();
      return d;
    }

    // Gave up waiting: say so and offer the same message again. Not saved to
    // history - after a page change the customer simply types again.
    function renderRetry(text, action) {
      renderError(t('late'));
      var wrap = el('div', 'remi-choices');
      var b = el('button', 'remi-choice');
      b.type = 'button';
      b.textContent = t('retry');
      b.addEventListener('click', function () {
        if (isLoading) return;
        wrap.remove();
        sendMessage(text, action, true);
      });
      wrap.appendChild(b);
      msgs.appendChild(wrap);
      scroll();
    }

    function sendMessage(text, action, isRetry) {
      if (isLoading) return;
      isLoading = true;
      sendBtn.disabled = true;

      clearChoices(msgs);
      if (!isRetry) {
        renderUserMsg(text);
        record({ k: 'user', t: text });
        flagCart('engaged');
      }
      replyTop = msgs.lastElementChild;
      var loader = addLoader();
      var slowNote = null;
      var live = liveReply(function () {
        loader.remove();
        if (slowNote) slowNote.remove();
      });
      var watch = watchReply(SLOW_MS, GIVE_UP_MS, function () { if (!live.shown()) slowNote = addSlowNote(); });
      var turn = action || { type: 'text', payload: text };

      var vars = varsDue ? pageVars() : null;
      varsDue = false;
      runTurn(api, ensureChat(preferencesAllowed()), turn, vars, watch.signal, live.add)
        .then(null, function (e) {
          // Cut off after part of the reply arrived (e.g. a held-open stream
          // hit the give-up limit): keep what the customer can already read.
          if (!live.shown()) throw e;
        })
        .then(function () {
          watch.done();
          var traces = live.finish();
          var shown = withPrompt(traces, lang);
          if (shown !== traces) {
            // withPrompt added a line (buttons only, or nothing at all): it
            // belongs above whatever this reply already drew.
            var line = el('div', 'remi-msg remi-msg--agent');
            renderFormattedText(shown[0].payload.message, line);
            msgs.insertBefore(line, replyTop && replyTop.isConnected ? replyTop.nextSibling : null);
            loader.remove();
            if (slowNote) slowNote.remove();
            scroll();
          }
          record({ k: 'traces', traces: keepTraces(shown) });
        })
        .catch(function (e) {
          watch.done();
          loader.remove();
          if (slowNote) slowNote.remove();
          if (vars) varsDue = true;   // the agent may not have them: send again
          if (isAbort(e)) renderRetry(text, action);
          else renderError(t('fail'));
        })
        .then(function () {
          isLoading = false;
          sendBtn.disabled = false;
        });
    }

    // Draws a streamed reply as it arrives: each completion grows one text
    // bubble; product cards wait for the next other trace so a row of cards
    // still renders as one carousel. finish() returns the reply as plain
    // traces (completions folded into text) for the checks and the history.
    function liveReply(onFirst) {
      var all = [];
      var cards = [];
      var bubble = null;
      var text = '';
      var drawn = false;
      function first() { if (!drawn) { drawn = true; onFirst(); } }
      function flushCards() { if (cards.length) { processTraces(cards); cards = []; } }
      return {
        shown: function () { return drawn; },
        add: function (trace) {
          all.push(trace);
          if (trace.type === 'completion') {
            var p = trace.payload || {};
            if (p.state === 'start') { flushCards(); bubble = null; text = ''; }
            else if (p.state === 'end') bubble = null;
            else if (p.state === 'content' && p.content) {
              text += p.content;
              if (!text.trim()) return;
              first();
              if (!bubble) { bubble = el('div', 'remi-msg remi-msg--agent'); msgs.appendChild(bubble); }
              bubble.textContent = '';
              renderFormattedText(text, bubble);
              scroll();
            }
            return;
          }
          if (trace.type === 'cardV2') { first(); cards.push(trace); return; }
          if (!DRAWN[trace.type]) return;
          flushCards();
          first();
          processTraces([trace]);
        },
        finish: function () {
          flushCards();
          return foldCompletions(all);
        }
      };
    }

    // The greeting is on screen: send the question a page button queued, if any.
    function greeted() {
      isGreeted = true;
      var q = asks.take();
      if (q) sendMessage(q);
    }

    // A page button (homepage concern) asks for the shopper: open the chat and
    // send the question once the greeting is there. Ignored while a reply loads.
    function askRemi(text) {
      if (isLoading || !asks.ask(text)) return;
      if (!isOpen) openPanel();
      if (isGreeted) greeted();
    }

    function launch() {
      if (isLaunched) return;
      isLaunched = true;
      var note = el('div', 'remi-ai-note');
      note.textContent = aiNotice(lang);
      msgs.appendChild(note);
      var loader = addLoader();
      // The greeting is short; if it has not come within LAUNCH_GIVE_UP_MS the
      // fixed fallback greeting below lets the customer start typing.
      var watch = watchReply(LAUNCH_GIVE_UP_MS, LAUNCH_GIVE_UP_MS, function () {});

      // Launch payload is kept too (harmless, and still visible via last_event);
      // the playbooks read the variables, which runTurn sends with the launch.
      var payload = {
        locale: locale,
        browserLanguage: browserLang,
        page_type: pageType,
        page_url: pageUrl,
        theme_role: themeRole
      };
      if (handle) payload.product_handle = handle;
      if (title)  payload.product_title  = title;
      if (type)   payload.product_type   = type;

      // Resolve the visitor ID against cookie consent, then launch. If the
      // launch fails the fixed greeting below still lets the customer type,
      // and their first message starts the session.
      new Promise(function (resolve) { withConsent(function (allowed) { resolve(ensureChat(allowed)); }); })
        .then(function (c) {
          // The greeting is short, so it is drawn whole, not as it streams.
          return runTurn(api, c, { type: 'launch', payload: payload }, pageVars(), watch.signal, function () {})
            .then(foldCompletions);
        })
        .then(function (traces) {
          watch.done();
          loader.remove();
          traces = withGreeting(traces, lang);
          processTraces(startersFor(chat && chat.items, traces, lang, title, pickName));
          record({ k: 'traces', traces: keepTraces(traces) });   // starters re-applied on redraw
          greeted();
        })
        .catch(function () {
          watch.done();
          loader.remove();
          renderError(t('hi'));
          // The starters still work: a tap sends the sentence, which starts the session.
          var fallback = startersFor([], [], lang, title, pickName);
          if (fallback.length) renderChoices(fallback[0].payload.buttons);
          greeted();
        });
    }

    // This tab's conversation, made by whichever comes first: the launch or a
    // message typed before the launch had its consent answer.
    function ensureChat(persistAllowed) {
      if (!uid) uid = getUserID(persistAllowed);
      if (!chat) chat = { uid: uid, open: isOpen, items: [] };
      return chat;
    }

    // The project variables the playbooks read - sent at launch, and with the
    // first message on every later page so "this product" follows the visitor.
    function pageVars() {
      return {
        browserLanguage: browserLang,
        detected_language: mapLanguage(browserLang) || 'en',
        store_locale: locale,
        page_type: pageType,
        page_url: pageUrl,
        theme_role: themeRole,
        product_handle: handle,
        product_title: title,
        product_type: type
      };
    }

    function record(item) {
      if (!chat) return;
      chat.items.push(item);
      saveHistory(tabStore, chat);
    }

    // Same tab, same visitor, earlier page: redraw the conversation, tell the
    // agent which page we are on now, and do NOT launch again (a launch restarts
    // the conversation from the greeting).
    function restore(saved) {
      chat = saved;
      uid = saved.uid;
      isLaunched = true;
      var note = el('div', 'remi-ai-note');
      note.textContent = aiNotice(lang);
      msgs.appendChild(note);
      startersOnRestore(replayItems(saved.items), lang, title, pickName).forEach(function (it) {
        if (it.k === 'user') renderUserMsg(it.t);
        else if (it.k === 'traces') processTraces(it.traces);
      });
      scroll();
      varsDue = true;
      isGreeted = true;
    }

    function openPanel() {
      isOpen = true;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      w.classList.add('remi-w--open');
      setTimeout(function () { input.focus(); }, 50);
      if (chat) { chat.open = true; saveHistory(tabStore, chat); }
      flagCart('opened');
      launch();
    }

    function closePanel() {
      isOpen = false;
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      w.classList.remove('remi-w--open');
      if (chat) { chat.open = false; saveHistory(tabStore, chat); }
    }

    var saved = loadHistory(tabStore);
    if (saved && !saved.items.length) saved = null;   // launch failed last time: start fresh
    if (saved) restore(saved);

    if (inline) {
      // Always-open, no toggle. Reveal the panel and launch immediately.
      isOpen = true;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      w.classList.add('remi-w--open');
      flagCart('opened');
      launch();
    } else {
      toggleBtn.addEventListener('click', function () { isOpen ? closePanel() : openPanel(); });
      closeBtn.addEventListener('click', closePanel);
      if (saved && saved.open) openPanel();
    }

    // Homepage: the launcher stays out of the way while the hero is on screen,
    // and appears once the shopper scrolls past it. No observer: always shown.
    var hero = pageType === 'index' && !inline && document.querySelector('[id*="__new_hero_section"]');
    if (hero && 'IntersectionObserver' in window) {
      w.classList.toggle('remi-w--hidden', heroInView(hero.getBoundingClientRect(), window.innerHeight));
      new IntersectionObserver(function (entries) {
        w.classList.toggle('remi-w--hidden', entries[0].isIntersecting);
      }).observe(hero);
    }

    // Buttons in the page: [data-remi-open] opens the chat, [data-remi-ask="frizz"]
    // opens it and asks that concern. Labels follow the widget's language.
    [].forEach.call(document.querySelectorAll('[data-remi-ask]'), function (b) {
      var c = (CONCERNS[lang] || CONCERNS.en)[b.getAttribute('data-remi-ask')];
      if (c) setText(b, c.label);
    });
    document.addEventListener('click', function (e) {
      var target = e.target && e.target.closest ? e.target : null;
      if (!target) return;
      if (target.closest('[data-remi-open]')) {
        if (!isOpen) openPanel();
        return;
      }
      var ask = target.closest('[data-remi-ask]');
      if (ask) {
        var c = (CONCERNS[lang] || CONCERNS.en)[ask.getAttribute('data-remi-ask')];
        if (c) askRemi(c.send);
      }
    });

    // Consent given or withdrawn mid-chat: keep the same ID, move its storage.
    document.addEventListener('visitorConsentCollected', function () {
      if (uid) uid = getUserID(preferencesAllowed());
    });

    sendBtn.addEventListener('click', function () {
      var t = input.value.trim();
      if (t && !isLoading) { input.value = ''; sendMessage(t); }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        var t = input.value.trim();
        if (t && !isLoading) { input.value = ''; sendMessage(t); }
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-remi-widget]').forEach(initWidget);
  }

  // Node/CommonJS: export the pure helpers for unit testing. Guarded so the
  // browser build is unaffected (`module` is undefined in the browser).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      toHttps: toHttps,
      chooseImage: chooseImage,
      extractHandle: extractHandle,
      mapLanguage: mapLanguage,
      aiNotice: aiNotice,
      withGreeting: withGreeting,
      withPrompt: withPrompt,
      withStarters: withStarters,
      withPickStarters: withPickStarters,
      heroInView: heroInView,
      setText: setText,
      askQueue: askQueue,
      CONCERNS: CONCERNS,
      PICK_STARTERS: PICK_STARTERS,
      startersOnRestore: startersOnRestore,
      startersFor: startersFor,
      readableTitle: readableTitle,
      STARTERS: STARTERS,
      parseSSE: parseSSE,
      turnBody: turnBody,
      vfStartSession: vfStartSession,
      vfTurn: vfTurn,
      runTurn: runTurn,
      foldCompletions: foldCompletions,
      followTop: followTop,
      clearChoices: clearChoices,
      watchReply: watchReply,
      isAbort: isAbort,
      I18N: I18N,
      SLOW_MS: SLOW_MS,
      GIVE_UP_MS: GIVE_UP_MS,
      avoidLift: avoidLift,
      loadHistory: loadHistory,
      saveHistory: saveHistory,
      keepTraces: keepTraces,
      replayItems: replayItems,
      HISTORY_KEY: HISTORY_KEY,
      resolveUserID: resolveUserID,
      buttonURL: buttonURL,
      flagUpgrade: flagUpgrade,
      tagItems: tagItems,
      flagCart: flagCart,
      FLAG_KEY: FLAG_KEY,
      UID_KEY: UID_KEY
    };
  }

  // Browser only: bootstrap the widget. Skipped under Node (no document), so
  // requiring this file for tests does not throw.
  if (typeof document !== 'undefined') {
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', init)
      : init();
  }
})();
